import PropTypes from 'prop-types';

import { confirm, IconCellRenderer, LinkCellRenderer } from '@trueprodigy/components';

import AgGrid from '@components/AgGrid/AgGrid';
import { Helmet } from 'react-helmet';

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import { fetchingState } from '@constants';

import { showError, showSuccess } from '@actions/global';
import * as ownerActions from '@modules/appraisal/actions/owner';
import * as ownerSelectors from '@modules/appraisal/selectors/owner';
import { propertySelector } from '@modules/appraisal/selectors/property';
import TextField from '@components/TextFieldCaseSetting';
import {
  viewOnlySelector,
} from '@modules/appraisal/selectors/global'
import {
  Button,
  CircularProgress,
  Fab,
  Tooltip
} from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';

import OwnerAliasHeader from './OwnerAliasHeader';
import {
  ContentWrapper
} from '../propertyPage/components/tableStyledComponents';

const buildColumnDefs = (dispatch, canEdit, openAliasDetail) => {
  let baseColumns = [
    {
        headerName: 'Sequence',
        field: 'sequence',
        cellRenderer: LinkCellRenderer,
        cellRendererParams: {
          onClick: async (_, data) => {
            await openAliasDetail(data);
          },
        },
    },
    { headerName: 'First Name', field: 'firstName' },
    {
        headerName: 'Middle Name',
        field: 'middleName',
    },
    {
        headerName: 'Last Name',
        field: 'lastName',
    },
    {
        headerName: 'Full Name',
        field: 'fullName',
    },
    {
        headerName: 'Delete',
        field: 'delete',
        cellRenderer: IconCellRenderer,
        cellRendererParams: {
          icons: [
            {
              icon: canEdit && 'fa-trash',
              onClick: async (_, data) => {
                try {
                  await confirm('alias');
                  await dispatch(
                    ownerActions.deleteOwnerNameAlias({
                      alternateNamesID: data.alternateNamesID,
                    }),
                  );
                  await dispatch(
                    ownerActions.fetchOwnerNameAliases({
                      ownerID: data.ownerID,
                    }));
                  dispatch(showSuccess('Owner Alias Deleted'));
                } catch (error) {
                  dispatch(showError());
                }
              },
            },
          ],
        },
    },
  ];
  
  if (canEdit) {
    baseColumns = [
      {
        headerName: '',
        field: 'alternateNamesID',
        rowDrag: true,
        width: 40,
        maxWidth: 40,
        minWidth: 40,
        suppressSizeToFit: true,
        sortable: false,
        suppressMovable: true,
        resizable: false,
      },
    ].concat(baseColumns);
  }

  return baseColumns;
};

export const STATES = {
  LIST_ALIASES: 'ListAliases',
  NEW_ALIAS: 'NewAlias',
  DETAIL_ALIAS: 'DetailAlias',
};

const OwnerAliasNames = ({ ownerID, isReadOnly }) => {
  const dispatch = useDispatch();
  const initialState = {
    state: STATES.LIST_ALIASES,
    alternateNamesID: '',
    ownerAlias: {}
  };
  const [viewState, setViewState] = useState(initialState);
  const updateViewState = (newState) => {
    console.log('New State');
    console.log(newState);
    setViewState(prevState => ({
      ...prevState,
      ...newState
    }));
  };

  const property = useSelector(propertySelector);
  const ownerAliases = useSelector(ownerSelectors.ownerAliasesSelector).toJS() ?? [];
  const ownerAliasesState = useSelector(ownerSelectors.ownerAliasesStateSelector);
  const viewOnly = useSelector(viewOnlySelector);

  const certified =
    property.getIn(['data', 'rollCorrGroupStatus']) === 'Certified';

  useEffect(() => {
    dispatch(ownerActions.fetchOwnerNameAliases({ ownerID }));
  }, []);

  const onUpdateAliasOrdering = async (event) => {
    const newOrder = [];
    event.api.forEachNodeAfterFilterAndSort((node) => {
      newOrder.push(node.data);
    });
    const alternateNamesIDs = newOrder.map(
      row => row.alternateNamesID,
    );
    await dispatch(ownerActions.reorderOwnerAliasNames({
      ownerID,
      payload: { alternateNameIDList: alternateNamesIDs },
    }));
    await dispatch(ownerActions.fetchOwnerNameAliases({ ownerID }));
  };

  const onCloseDetail = async () => {
    updateViewState({
      state: STATES.LIST_ALIASES,
      alternateNamesID: '',
      ownerAlias: {}
    });
  };

  const openAliasNew = async () => {
    console.log('New Alias');
    updateViewState({
        state: STATES.NEW_ALIAS,
        alternateNamesID: '',
        ownerAlias: {
          alternateNamesID: 0,
          firstName: '',
          middleName: '',
          lastName: '',
          fullName: '',
          sequence: 0
        }
      });
  };

  const isValid = viewState.ownerAlias
    && viewState.ownerAlias.firstName
    && viewState.ownerAlias.lastName
    && viewState.ownerAlias.fullName;

  const openAliasDetail = async (ownerAlias) => {
    console.log('Detail Alias');
    updateViewState({
        state: STATES.DETAIL_ALIAS,
        alternateNamesID: ownerAlias.alternateNamesID,
        ownerAlias: {
          ...ownerAlias
        }
      });
  };

  const handleCreateSaveClick = async () => {
    if (!viewState.alternateNamesID) {
      await dispatch(ownerActions.createOwnerNameAlias({
        ownerID,
        payload: {
          ...viewState.ownerAlias
        }
      }));
    } else {
      await dispatch(ownerActions.updateOwnerNameAlias({
        alternateNamesID: viewState.alternateNamesID,
        payload: {
          ...viewState.ownerAlias,
          lastUpdate: viewState.ownerAlias.updateDt
        }
      }));
    }
    await dispatch(ownerActions.fetchOwnerNameAliases({ ownerID }));
    await onCloseDetail();
  };

  return (
    <Wrapper>
      {viewState.state === STATES.LIST_ALIASES 
      ? <>
        {ownerAliasesState === fetchingState.FETCHING
        ?
          <LoadingWrapper>
            <CircularProgress />
          </LoadingWrapper>
        :
        <ContentWrapper>
          <ListingControls>
            <Title>OWNER ALIASES</Title>
            <Tooltip title="Add Event">
              <Fab
                aria-label="Add Event"
                color="primary"
                disabled={certified || isReadOnly}
                onClick={openAliasNew}
              >
                <AddIcon />
              </Fab>
            </Tooltip>
          </ListingControls>
          <TableContainer 
            className="ag-theme-material"
            preview={false}
            height="250px"
          >
            <AgGrid
              name="propertyOwnerAliases"
              columnDefs={buildColumnDefs(
                dispatch,
                !certified,
                openAliasDetail
              )}
              defaultColDef={{ resizable: true, sortable: true }}
              rowData={ownerAliases}
              disablePreferenceSave
              gridOptions={{
                onRowDragEnd: certified ? undefined : onUpdateAliasOrdering,
              }}
              rowDragManaged={certified ? undefined : true}
            />
          </TableContainer>
        </ContentWrapper>
        }
        </>
      : <ContentWrapper>
          <Helmet>
            <title>Owner Alias Record</title>
          </Helmet>
          {viewState.alternateNamesID &&
            viewState.ownerAlias &&
            !viewState.ownerAlias.alternateNamesID ? (
            <LoadingWrapper>
              <CircularProgress />
            </LoadingWrapper>
          ) : (
            <>
              <OwnerAliasHeader
                alternateNamesID={viewState.alternateNamesID}
                ownerAlias={viewState.ownerAlias}
                onClose={onCloseDetail}
              />
              <FormContainer>
                <Row>
                  <Label>First Name:</Label>
                  <TextField
                    fullWidth
                    disabled={viewOnly}
                    value={viewState.ownerAlias.firstName}
                    onChange={ e => updateViewState({ ownerAlias: { ...viewState.ownerAlias, firstName: e.target.value } })}
                    label=""
                    placeholder="First Name"
                    meta={{}}
                    name="firstName"
                  />
                </Row>
                <Row>
                  <Label>Middle Name:</Label>
                  <TextField
                    fullWidth
                    disabled={viewOnly}
                    value={viewState.ownerAlias.middleName}
                    onChange={ e => updateViewState({ ownerAlias: { ...viewState.ownerAlias, middleName: e.target.value } })}
                    label=""
                    placeholder="Middle Name"
                    meta={{}}
                    name="middleName"
                  />
                </Row>
                <Row>
                  <Label>Last Name:</Label>
                  <TextField
                    fullWidth
                    disabled={viewOnly}
                    value={viewState.ownerAlias.lastName}
                    onChange={ e => updateViewState({ ownerAlias: { ...viewState.ownerAlias, lastName: e.target.value } })}
                    label=""
                    placeholder="Last Name"
                    meta={{}}
                    name="lastName"
                  />
                </Row>
                <Row>
                  <Label>Full Name:</Label>
                  <TextField
                    fullWidth
                    disabled={viewOnly}
                    value={viewState.ownerAlias.fullName}
                    onChange={ e => updateViewState({ ownerAlias: { ...viewState.ownerAlias, fullName: e.target.value } })}
                    label=""
                    placeholder="Full Name"
                    meta={{}}
                    name="fullName"
                  />
                </Row>
                <ButtonContainer>
                  <Button
                    color="primary"
                    
                    variant="contained"
                    onClick={handleCreateSaveClick}
                    disabled={!isValid || viewOnly}
                  >
                    {viewState.alternateNamesID ? 'Save' : 'Create'}
                  </Button>
                </ButtonContainer>
              </FormContainer>
            </>
          )}
        </ContentWrapper>
      }
    </Wrapper>
  );
};

OwnerAliasNames.propTypes = {
  ownerID: PropTypes.string,
  isReadOnly: PropTypes.any,
};

const Wrapper = styled.div`
  padding-top: 0;
  background-color: #eee;
`;

const ListingControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  margin-left: 20px;
`;

export const TableContainer = styled.div`
  height: ${({ height }) => height || '200px'};
  margin: 10px 4px 4px 4px;
  filter: ${({ preview }) => (preview ? 'blur(2px)' : null)};
  pointer-events: ${({ preview }) => (preview ? 'none' : 'auto')};

  & .ag-rich-select .ag-rich-select-list {
    width: ${({ wideSelect }) =>
      wideSelect ? '400px !important' : '200px !important'};
  }
  margin-left: 20px;
`;

const LoadingWrapper = styled.div`
  min-height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 20px 0;
  width: 100%;
  padding-left: 20px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  width: 100%;
`;

const Label = styled.div`
  width: 150px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 10px;
`;

const Title = styled.h4`
  flex: ${({ flex }) => flex || 1};
  font-family: Roboto, sans-serif;
  visibility: visible;
  font-size: 24px;
  font-weight: 500;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.87);
`;

export default OwnerAliasNames;
